import { isLightweightWithTypeAndHints } from "@/store/lightweight-elements-discriminators";
import {
  selectLightweightAncestor,
  selectLightweightElementsSlice,
} from "@/store/lightweight-elements-selectors";
import { selectNewestMergedRevision } from "@/store/revisions-selector";
import { IElementType, IElementTypeHint } from "@faro-lotv/ielement-types";
import { SINGLE_SCAN_TYPE_HINTS } from "@faro-lotv/project-source";
import { createSelector } from "@reduxjs/toolkit";

/** Datasets holding a project point cloud, as in `selectPointCloudType` of `@faro-lotv/project-source`. */
const PROJECT_POINT_CLOUD_DATASET_TYPE_HINTS = [
  IElementTypeHint.dataSetFocus,
  IElementTypeHint.dataSetEls,
  IElementTypeHint.dataSetOrbis,
  IElementTypeHint.dataSetSiteScape,
  IElementTypeHint.dataSetRovi,
  IElementTypeHint.dataSetCapture,
  IElementTypeHint.dataSetWs,
];

/** Sections below a dataset whose point clouds are not the project point cloud. */
const NON_PROJECT_POINT_CLOUD_TYPE_HINTS = [
  ...SINGLE_SCAN_TYPE_HINTS,
  IElementTypeHint.flash,
];

/** The outputs that previous publishings left in the project. */
export type PublishedOutputs = {
  /** Whether the project contains a project point cloud, i.e. the "3D view". */
  pointCloud: boolean;
  /** Whether the project contains generated (i.e. not imported) Gaussian splats. */
  gaussianSplats: boolean;
};

/**
 * @param state The current application state.
 * @returns The outputs of the previous publishings, or `undefined` if the project was never published
 *  or its lightweight structure is not loaded.
 */
export const selectPublishedOutputs = createSelector(
  [selectNewestMergedRevision, selectLightweightElementsSlice],
  (mergedRevision, slice): PublishedOutputs | undefined => {
    const elements = Object.values(slice.lightweightElements);
    if (!mergedRevision || !elements.length) return;

    const store = { lightweightElements: slice };
    const output: PublishedOutputs = {
      pointCloud: false,
      gaussianSplats: false,
    };
    for (const element of elements) {
      if (
        element?.type === IElementType.pointCloudStream ||
        element?.type === IElementType.pointCloudStreamWebShare
      ) {
        // The closest of these sections decides whether it's a project point cloud.
        const section = selectLightweightAncestor(element, (el) =>
          isLightweightWithTypeAndHints(el, IElementType.section, [
            ...PROJECT_POINT_CLOUD_DATASET_TYPE_HINTS,
            ...NON_PROJECT_POINT_CLOUD_TYPE_HINTS,
          ]),
        )(store);
        output.pointCloud ||=
          !!section &&
          isLightweightWithTypeAndHints(
            section,
            IElementType.section,
            PROJECT_POINT_CLOUD_DATASET_TYPE_HINTS,
          );
      } else if (element?.type === IElementType.gaussianSplats) {
        output.gaussianSplats ||= !selectLightweightAncestor(
          element,
          (el) => el.typeHint === IElementTypeHint.dataSetGaussianSplats,
        )(store);
      }
    }
    return output;
  },
);
